import { useMemo } from 'react';
import { Provider } from 'react-redux';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { MemoryRouter } from 'react-router';
import { queryClient } from '../../../../App';
import { getStore } from '../../../../store/store';
import { periodsQueryOptions } from 'components/IAP/hooks/incidentHooks';
import { IcsFormPdfPreviewProvider } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  useGetPositionsForIncidentId,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import { useIncidentSitrep } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  IncidentListItem,
  type IncidentListItemData,
} from './IncidentListItem';

type IncidentPopupContentProps = {
  incident: IncidentListItemData;
};

const IncidentPopupContentInner: React.FC<IncidentPopupContentProps> = ({
  incident,
}) => {
  const { data: periods = [] } = useQuery(
    periodsQueryOptions({ incidentId: incident.id, enabled: !!incident.id })
  );
  const { data: roster = [] } = useGetRosterForIncident(incident.id);
  const { data: positions = [] } = useGetPositionsForIncidentId(incident.id);
  const { data: sitrep } = useIncidentSitrep(incident.id);

  const currentPeriod = useMemo(
    () => periods.find((period) => period.id === incident.currentPeriodId),
    [incident.currentPeriodId, periods]
  );

  const currentPeriodNumber = currentPeriod?.period_number ?? 0;

  const currentOperationalPeriod = useMemo(() => {
    if (!incident.currentPeriodId) return NULL_VALUE;
    if (!currentPeriod?.period_number) return NULL_VALUE;

    const start = currentPeriod.start_date_time
      ? dayjs(currentPeriod.start_date_time).format('MM/DD HH:mm')
      : null;
    const end =
      currentPeriod.anticipated_end_date_time ?? currentPeriod.end_date_time;
    const endFormatted = end ? dayjs(end).format('MM/DD HH:mm') : null;

    const range =
      start && endFormatted
        ? ` | ${start} – ${endFormatted}`
        : start
          ? ` | ${start}`
          : '';

    return `Period ${currentPeriod.period_number}${range}`;
  }, [currentPeriod, incident.currentPeriodId]);

  const incidentCommander = useMemo(() => {
    const incidentCommanderPosition = positions.find(
      (position) => position.name === 'Incident Commander'
    );

    const commander = roster.find((member) =>
      member.incidentPositions.some(
        (memberPosition) =>
          (incidentCommanderPosition?.id &&
            memberPosition.positionId === incidentCommanderPosition.id) ||
          memberPosition.positionName === 'Incident Commander'
      )
    );

    return commander?.userName || commander?.emailAddress || NULL_VALUE;
  }, [positions, roster]);

  return (
    <div style={{ maxWidth: '420px' }}>
      <IncidentListItem
        incident={incident}
        currentOperationalPeriod={currentOperationalPeriod}
        currentPeriodNumber={currentPeriodNumber}
        incidentCommander={incidentCommander}
        sitrep={sitrep ?? null}
        showWorkspaceButton={false}
      />
    </div>
  );
};

export const IncidentPopupContent: React.FC<IncidentPopupContentProps> = (
  props
) => {
  const store = getStore();

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <MemoryRouter>
          <IcsFormPdfPreviewProvider>
            <IncidentPopupContentInner {...props} />
          </IcsFormPdfPreviewProvider>
        </MemoryRouter>
      </QueryClientProvider>
    </Provider>
  );
};
